export interface MaintenanceState {
  isUnderMaintenance: boolean;
  message: string;
}

type MaintenanceListener = (state: MaintenanceState) => void;

const DEFAULT_MAINTENANCE_MESSAGE =
  "We're currently performing scheduled maintenance to improve your experience.\nWe'll be back shortly. Thank you for your patience.";

let maintenanceState: MaintenanceState = {
  isUnderMaintenance: false,
  message: DEFAULT_MAINTENANCE_MESSAGE,
};

const listeners: MaintenanceListener[] = [];

const normalizeMessage = (message?: unknown): string => {
  if (typeof message !== 'string' || !message.trim()) {
    return DEFAULT_MAINTENANCE_MESSAGE;
  }

  return message.replace(/<br\s*\/?>/gi, '\n').trim();
};

const parseMaintenanceFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue === '1' || normalizedValue === 'true';
  }
  return false;
};

const readMessage = (payload: any): string | undefined =>
  payload?.message ?? payload?.data?.message;

const readFlag = (payload: any, status?: number, headers?: any): boolean => {
  if (Number(status) === 503) return true;

  const headerFlag =
    headers?.is_under_maintenance ??
    headers?.Is_under_maintenance ??
    headers?.IS_UNDER_MAINTENANCE;

  if (parseMaintenanceFlag(headerFlag)) return true;

  return parseMaintenanceFlag(
    payload?.under_maintenance ??
      payload?.is_under_maintenance ??
      payload?.data?.under_maintenance ??
      payload?.data?.is_under_maintenance,
  );
};

const notify = () => {
  listeners.forEach((listener) => listener(maintenanceState));
};

const setState = (isUnderMaintenance: boolean, message?: unknown): boolean => {
  const nextState: MaintenanceState = {
    isUnderMaintenance,
    message: isUnderMaintenance
      ? normalizeMessage(message)
      : DEFAULT_MAINTENANCE_MESSAGE,
  };

  if (
    maintenanceState.isUnderMaintenance === nextState.isUnderMaintenance &&
    maintenanceState.message === nextState.message
  ) {
    return nextState.isUnderMaintenance;
  }

  maintenanceState = nextState;
  notify();
  return maintenanceState.isUnderMaintenance;
};

const maintenanceManager = {
  getState(): MaintenanceState {
    return maintenanceState;
  },

  isUnderMaintenance(): boolean {
    return maintenanceState.isUnderMaintenance;
  },

  getMessage(): string {
    return maintenanceState.message;
  },

  getBlockedResponse() {
    return {
      status: 503,
      under_maintenance: true,
      is_under_maintenance: true,
      message: maintenanceState.message,
    };
  },

  subscribe(listener: MaintenanceListener) {
    listeners.push(listener);
    listener(maintenanceState);

    return () => {
      const index = listeners.indexOf(listener);
      if (index >= 0) listeners.splice(index, 1);
    };
  },

  update(isUnderMaintenance: boolean, message?: unknown): boolean {
    return setState(isUnderMaintenance, message);
  },

  isMaintenanceResponse(payload: any, status?: number, headers?: any): boolean {
    return readFlag(payload, status, headers);
  },

  applyResponse(payload: any, status?: number, headers?: any): boolean {
    return setState(readFlag(payload, status, headers), readMessage(payload));
  },
};

export default maintenanceManager;
